import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { AssetGroup } from '@play-co/timestep-core';
import SpinnerLocal from 'src/game/components/shared/SpinnerLocal';
import { getScreenDimensions } from 'src/lib/utils';

export const RESUBSCRIBE_HINT_KEYS = [
  'pet_messages',
  'dont_miss',
  'friend_gifts',
  'new_events',
  'dont_miss_tap',
] as const;

export type ResubscribeHintKey = (typeof RESUBSCRIBE_HINT_KEYS)[number];

const HINT_I18N_KEY: Record<ResubscribeHintKey, string> = {
  pet_messages: 'resubscribe.hintOverlay.hints.petMessages',
  dont_miss: 'resubscribe.hintOverlay.hints.dontMiss',
  friend_gifts: 'resubscribe.hintOverlay.hints.friendGifts',
  new_events: 'resubscribe.hintOverlay.hints.newEvents',
  dont_miss_tap: 'resubscribe.hintOverlay.hints.dontMissTap',
};

type OpenArgs = { hintID: ResubscribeHintKey };

const DIALOG_FRAME = 'assets/ui/resubscribe/dialog_frame.png';
const DIALOG_FRAME_NATURAL_WIDTH = 945;
const DIALOG_FRAME_NATURAL_HEIGHT = 340;
const DIALOG_FRAME_MAX_WIDTH = 640;

// Single knob to shift the whole cluster (bubble + wait text + spinner)
// up (negative) or down (positive) in pixels.
const CLUSTER_Y_OFFSET = -150;

export default class PopupResubscribeHintOverlay extends PopupBasic<
  () => void
> {
  private hintText: LangBitmapFontTextView;
  private spinner: SpinnerLocal;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      ...opts,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      closeableWithBg: false,
      zIndex: 10020,
      tracking: {
        feature: 'resubscribe',
        $subFeature: 'resubscribe-hint-overlay',
      },
    });

    this.buttonClose.removeFromSuperview();

    this.bg.updateOpts({ backgroundColor: 'rgba(75,56,96,0.9)' });

    const bubbleScale = Math.min(
      1,
      (Math.min(this.box.style.width - 80, DIALOG_FRAME_MAX_WIDTH) * 1) /
        DIALOG_FRAME_NATURAL_WIDTH,
    );
    const bubbleWidth = DIALOG_FRAME_NATURAL_WIDTH * bubbleScale;
    const bubbleHeight = DIALOG_FRAME_NATURAL_HEIGHT * bubbleScale;
    const bubbleY = getScreenDimensions().height * 0.35 + CLUSTER_Y_OFFSET;
    // Preserve current visual spacing below the bubble.
    const waitY = bubbleY + 194;
    const spinnerCenterY = bubbleY + 361;

    const bubble = new ImageScaleView({
      superview: this.box,
      image: DIALOG_FRAME,
      width: bubbleWidth,
      height: bubbleHeight,
      x: this.box.style.width / 2,
      y: bubbleY,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    this.hintText = new LangBitmapFontTextView({
      superview: bubble,
      x: bubbleWidth * 0.3,
      y: bubbleHeight * 0.15,
      width: bubbleWidth * 0.65,
      height: bubbleHeight * 0.55,
      align: 'center',
      verticalAlign: 'center',
      size: 44,
      color: '#91808C',
      font: 'Body',
      wordWrap: true,
      canHandleEvents: false,
      localeText: () => '',
    });

    new LangBitmapFontTextShadowView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: waitY,
      width: 600,
      height: 80,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 60,
      color: '#FFFDA1',
      textShadowColor: 'rgba(0,0,0,0.25)',
      textShadowOffsetY: 4,
      font: 'Title',
      canHandleEvents: false,
      localeText: () => i18n('resubscribe.hintOverlay.wait'),
    });

    this.spinner = new SpinnerLocal({
      superview: this.box,
      size: 134,
      offset: {
        x: 0,
        y: spinnerCenterY - this.box.style.height / 2,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      DIALOG_FRAME,
      'assets/ui/shared/icons/loading_spinner.png',
    ]);
  }

  public override init(args: OpenArgs) {
    this.hintText.localeText = () => i18n(HINT_I18N_KEY[args.hintID]);
    super.init({});
    this.spinner.start();
  }
}
